// @ts-check
// Botón "Traducir a…" y su diálogo (DEC-107).
//
// Vive en `features` y es PURO respecto al editor: recibe una función que le
// da el programa actual y otra que sabe entregar el resultado en el editor de
// destino. No conoce ninguno de los tres editores, que es lo que permite que
// los tres monten exactamente el mismo botón.
//
// ## Por qué el resultado se ENSEÑA antes de abrirlo
//
// Traducir y saltar directamente a la otra pantalla dejaría al alumno mirando
// un programa que no ha escrito él, sin haber visto de dónde sale. El diálogo
// muestra el resultado y él decide si abrirlo. Además, si lo que quería era
// solo mirar cómo se escribe su algoritmo en el otro lenguaje, no tiene que
// cambiar de pantalla para nada.
//
// ## Por qué avisa antes de reemplazar
//
// Abrir la traducción en el editor de destino PISA lo que hubiera allí. Se
// dice con todas las letras antes, no después.

import { crearModalDialogo } from '../../components/modal-dialog.js';
import { createSafeElement } from '../../security/safe-dom.js';
import { NOMBRE_REPRESENTACION, destinosDesde, traducir } from './index.js';
import './translation-panel.css';

/** @typedef {import('./index.js').Representacion} Representacion */
/** @typedef {ReturnType<typeof crearModalDialogo>} ModalDialogo */

/**
 * Crea el botón de traducción.
 *
 * @param {{
 *   desde: Representacion,
 *   obtenerFuente: () => unknown,
 *   entregar: (hacia: Representacion, resultado: any) => void,
 *   traducirFn?: typeof traducir,
 * }} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
export function crearBotonTraducir(opciones) {
  const { desde, obtenerFuente, entregar } = opciones;
  const traducirCon = opciones.traducirFn ?? traducir;

  const boton = /** @type {HTMLButtonElement} */ (
    createSafeElement('button', {
      className: 'traduccion-boton ui-button ui-button-secundario',
      text: 'Traducir a…',
      attrs: {
        type: 'button',
        'aria-label': `Traducir a… este ${NOMBRE_REPRESENTACION[desde]} a otra representación`,
      },
    })
  );

  /** @type {(() => void) | null} */
  let cerrarModal = null;

  /**
   * @param {ModalDialogo} modal
   * @param {Representacion} hacia
   */
  function traducirYMostrar(modal, hacia) {
    const resultado = traducirCon({ desde, hacia, fuente: obtenerFuente() });
    const cuerpo = createSafeElement('div', { className: 'traduccion-cuerpo' });

    if (!resultado.ok) {
      cuerpo.append(
        createSafeElement('p', {
          className: 'traduccion-aviso',
          attrs: { role: 'alert' },
          text: `No se puede convertir a ${NOMBRE_REPRESENTACION[hacia]} sin cambiar lo que hace tu programa:`,
        }),
      );
      const lista = createSafeElement('ul', { className: 'traduccion-motivos' });
      for (const motivo of resultado.motivos) {
        const item = createSafeElement('li', { text: motivo.descripcion });
        if (motivo.donde) {
          item.append(createSafeElement('span', { className: 'traduccion-donde', text: ` (${motivo.donde})` }));
        }
        lista.append(item);
      }
      cuerpo.append(lista);
      cuerpo.append(
        createSafeElement('p', {
          className: 'traduccion-ayuda',
          text: 'Se prefiere no convertir nada antes que entregarte un programa que hace algo distinto del tuyo.',
        }),
      );
      modal.cuerpo.replaceChildren(cuerpo);
      const volver = createSafeElement('button', { className: 'traduccion-boton', text: 'Volver', attrs: { type: 'button' } });
      volver.addEventListener('click', () => pintarEleccion(modal));
      modal.acciones.replaceChildren(volver);
      volver.focus();
      return;
    }

    modal.fijarTitulo(`Tu programa en ${NOMBRE_REPRESENTACION[hacia]}`);
    if (hacia === 'diagrama') {
      cuerpo.append(
        createSafeElement('p', {
          text: 'Tu programa se ha convertido a un diagrama de flujo. Ábrelo en el editor para verlo.',
        }),
      );
    } else {
      const pre = createSafeElement('pre', { className: 'traduccion-vista-previa' });
      pre.append(createSafeElement('code', { text: String(resultado.valor) }));
      pre.tabIndex = 0;
      // Es una región desplazable: sin foco propio, quien navega con teclado
      // no podría leer un programa largo.
      pre.setAttribute('role', 'region');
      pre.setAttribute('aria-label', `Código en ${NOMBRE_REPRESENTACION[hacia]}`);
      cuerpo.append(pre);
    }
    cuerpo.append(
      createSafeElement('p', {
        className: 'traduccion-aviso-reemplazo',
        text: `Si lo abres, sustituirá lo que tengas ahora en el editor de ${NOMBRE_REPRESENTACION[hacia]}.`,
      }),
    );
    modal.cuerpo.replaceChildren(cuerpo);

    const volver = createSafeElement('button', {
      className: 'traduccion-boton traduccion-boton-sec',
      text: 'Volver',
      attrs: { type: 'button' },
    });
    volver.addEventListener('click', () => pintarEleccion(modal));
    const abrir = createSafeElement('button', {
      className: 'traduccion-boton',
      text: `Abrir en ${NOMBRE_REPRESENTACION[hacia]}`,
      attrs: { type: 'button' },
    });
    abrir.addEventListener('click', () => {
      entregar(hacia, resultado.valor);
    });
    modal.acciones.replaceChildren(volver, abrir);
    abrir.focus();
  }

  /** @param {ModalDialogo} modal */
  function pintarEleccion(modal) {
    modal.fijarTitulo('Traducir a…');
    const cuerpo = createSafeElement('div', { className: 'traduccion-cuerpo' });
    cuerpo.append(
      createSafeElement('p', {
        text: 'El mismo algoritmo, escrito de otra forma. Elige a qué representación quieres verlo:',
      }),
    );
    const opcionesLista = createSafeElement('div', { className: 'traduccion-opciones' });
    for (const hacia of destinosDesde(desde)) {
      const opcion = createSafeElement('button', {
        className: 'traduccion-opcion',
        text: NOMBRE_REPRESENTACION[hacia],
        attrs: { type: 'button' },
      });
      opcion.addEventListener('click', () => traducirYMostrar(modal, hacia));
      opcionesLista.append(opcion);
    }
    cuerpo.append(opcionesLista);
    modal.cuerpo.replaceChildren(cuerpo);

    const cerrar = createSafeElement('button', {
      className: 'traduccion-boton traduccion-boton-sec',
      text: 'Cancelar',
      attrs: { type: 'button' },
    });
    cerrar.addEventListener('click', () => cerrarModal?.());
    modal.acciones.replaceChildren(cerrar);
    /** @type {HTMLElement} */ (opcionesLista.firstElementChild)?.focus();
  }

  const alPulsar = () => {
    let cerrando = false;
    const modal = crearModalDialogo({
      titulo: 'Traducir a…',
      onCerrar: () => cerrarDelTodo(),
    });
    const cerrarDelTodo = () => {
      if (cerrando) return;
      cerrando = true;
      modal.cuerpo.replaceChildren();
      modal.acciones.replaceChildren();
      modal.cerrar();
      globalThis.setTimeout(() => modal.destruir(), 0);
      cerrarModal = null;
    };
    cerrarModal = cerrarDelTodo;
    document.body.append(modal.elemento);
    modal.abrir(boton);
    pintarEleccion(modal);
  };
  boton.addEventListener('click', alPulsar);

  return {
    elemento: boton,
    destruir: () => {
      cerrarModal?.();
      boton.removeEventListener('click', alPulsar);
      boton.remove();
    },
  };
}
